import { useState, useCallback } from 'react';
import { useLocation } from "react-router";
import Config from "../config.json";
import axios from "axios";

const USE_SEARCH_LOGS = false;
  
const folders = Config.storage.folders
  .filter((f) => f.hidden !== true)
  .sort((a, b) => a.name.localeCompare(b.name));

function createDateFilter(date1, date2, fieldname) {
  if(USE_SEARCH_LOGS) console.log("createDateFilter", {date1, date2, fieldname});
  if ((date1 === undefined || date1 === "") && (date2 === undefined || date2 === "")) {
    return ""
  } else if ((date1 === undefined || date1 === "")) {
    return fieldname + " le " +  endOfDay(date2)
  } else if ((date2 === undefined || date2 === "")) {
    return fieldname + " ge " + startOfDay(date1)
  } else {
    if(USE_SEARCH_LOGS) console.log(startOfDay(date1), endOfDay(date2));
    return fieldname + " ge " + startOfDay(date1) + " and " + fieldname + " le " +  endOfDay(date2);
  }
}

function endOfDay(date) {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.toISOString();
}

function startOfDay(date) {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  return startOfDay.toISOString();
}

function useSearch() {
  const location = useLocation();

  const [fetchError, setFetchError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [totalCountOfMatches, setTotalCountOfMatches] = useState(0);
  const [isFirstSearchComplete, setIsFirstSearchComplete] = useState(false);

//#region Initialization Helpers
  const initializeFilter = useCallback((folder = null) => {
    if (!folders.map((folder) => folder.routePath).includes(location.pathname)) return {keywords: ""};
    var item = null;
    if (!folder) {
      item = folders.map((f) => ({ id: f.name, routepath: f.routePath, metadata: f.metadata }))
        .find(f => f.routepath === location.pathname)
        .metadata;
    } else {
      item = folders.map((f) => ({ id: f.name, routepath: f.routePath, metadata: f.metadata }))
        .find(f => f.id === folder)
        .metadata;
    }
    var filter = item.reduce((acc, field) => {
      if(field.searchable === true){
        acc[field.id] = "";
        return acc;
      }
      return acc;
    }, {});
    filter.keywords = "";
    return filter;
  }, [location.pathname]);
  
  const initializeSortType = useCallback((routePath = null) => {
    if (!folders.map((folder) => folder.routePath).includes(location.pathname)) return "";

    const folderConfig = folders.find(
      (f) => f.routePath === (routePath || location.pathname)
    );
    return folderConfig.defaultSortKey + "_" + folderConfig.defaultSortDatatype;
  }, [location.pathname]);

  const initializeSortOrder = useCallback((routePath = null) => {
    if (!folders.map((folder) => folder.routePath).includes(location.pathname)) return "asc";

    const folderConfig = folders.find(
      (f) => f.routePath === (routePath || location.pathname)
    );
    return folderConfig.defaultSortAsc ? "asc" : "desc";
  }, [location.pathname]);
  //#endregion

//#region Filters
  
  const [filters, setFilters] = useState(initializeFilter());
  const [sortType, setSortType] = useState(initializeSortType());
  const [sortOrder, setSortOrder] = useState(initializeSortOrder());

  const updateFilterValue = useCallback((object, type, fieldname) => {
     if (type === "keywords") {
      setFilters(prev => ({
        ...prev,
        "keywords": object.target.value
      }));
      return;
    }

    switch(type) {
      case "text":
        setFilters(prev => ({
          ...prev,
          [fieldname]: object.target.value
        }));
        break;
      case "datetime":
        const dates = object.getDates();
        const date1 = dates[0];
        const date2 = dates[1];
        if(USE_SEARCH_LOGS) console.log("datetime change:", { date1, date2 });
        
        if (!date1 && !date2) {
          setFilters(prev => {
            const updated = { ...prev };
            delete updated[fieldname];
            return updated;
          });
          return;
        }
        
        setFilters(prev => {
          const updated = { ...prev };
          updated[fieldname] = [
            date1 ? date1.toISOString() : "",
            date2 ? date2.toISOString() : ""
          ];
          return updated;
        });
        return;
      default:
        setFilters(prev => ({
          ...prev,
          [fieldname]: object.target.value
        }));
        return;
    }
  }, []);

  const getFilterString = useCallback((folder, filtersOverride = null) => {
    // Get the filter keys (use location?)
    let validRoute = false;
    folders.forEach(f => {
      if (f.routePath.includes(folder)) validRoute = true;
    });
    if (!validRoute) return "";

    var item = folders.map((folder) => ({ id: folder.name, routepath: folder.routePath, metadata: folder.metadata }))
        .find(f => f.routepath.includes(folder))
        .metadata;
    let filterString = "prefix eq '" + folder + "'";
    const filtersToUse = filtersOverride || filters;
    for (const metadata of item) {
      if ((filtersToUse[metadata.id] !== undefined || (filtersToUse[metadata.id] && filtersToUse[metadata.id][0] !== undefined) || (filtersToUse[metadata.id] && filtersToUse[metadata.id][1] !== undefined)) 
          && (filtersToUse[metadata.id] !== "" || filtersToUse[metadata.id+"1"] !== undefined || filtersToUse[metadata.id+"2"] !== undefined)) {
        filterString += " and ";
        if (metadata.inputType === "datetime") {
          filterString += createDateFilter(filtersToUse[metadata.id][0], filtersToUse[metadata.id][1], metadata.id);
        } else {
          filterString += metadata.id + " eq '" + filtersToUse[metadata.id] + "'";
        }
      }
    }
    return filterString;
  }, [filters]);

  const clearDocuments = useCallback(() => {
    setDocuments([]);
    setTotalCountOfMatches(0);
  }, []);

//#endregion

//#region Document Search
const getDocuments = useCallback(async (folder, top, sortType, sortOrder="asc", skip, isFirstSearch = false, filtersOverride = null) => {
    if (USE_SEARCH_LOGS) console.log("App => getDocuments()", { folder, top, sortType, sortOrder, isFirstSearch });
    setIsSearching(true);
    const header = {
      "api-key": process.env.REACT_APP_SEARCH_SERVICE_API_KEY,
      "content-type": "application/json",
    };
    let body = Object.assign({}, Config.search.requestBody);
    body.top = top;
    if (isFirstSearch !== true) body.skip = skip;
    body.filter = filtersOverride ? getFilterString(folder, filtersOverride) : getFilterString(folder);
    body.search = filtersOverride ? filtersOverride.keywords : filters.keywords;
    
    if (sortType) {
      const fieldName = sortType.split("_")[0];
      body.orderby = `${fieldName} ${sortOrder}`;
    }

    axios
      .post(
        process.env.REACT_APP_SEARCH_SERVICE_URL, 
        body, 
        {
          headers: header,
        })
      .then((res) => res.data)
      .then(
        (result) => {
          if (USE_SEARCH_LOGS) console.log(result);
          if (result["@odata.count"] !== undefined) {
            setTotalCountOfMatches(result["@odata.count"]);
          }
          setIsSearching(false);
          setIsFirstSearchComplete(true);
          setDocuments(prevDocs => prevDocs.concat(result.value));
          setFetchError(null);
        },
        (error) => {
          if (USE_SEARCH_LOGS) console.log(
            "Error loading additional results. Error message: " + error
          );
          setFetchError(error);
          setIsSearching(false);
        }
      );
  }, [filters.keywords, getFilterString ]);


//#endregion

//#region Interface
const searchAfterFilter = useCallback((folder) => {
  clearDocuments();
  setSortType(initializeSortType());
  setSortOrder(initializeSortOrder());
  getDocuments(
        folder,
        Config.search.resultsPerPage * Config.search.pageRangeDisplayed,
        initializeSortType(),
        initializeSortOrder(),
        0,
        true
  );
}, [initializeSortType, initializeSortOrder, clearDocuments, getDocuments]);

  const searchAfterNavigate = useCallback((folder) => {
    clearDocuments();
    setFilters(initializeFilter());
    setSortType(initializeSortType());
    setSortOrder(initializeSortOrder());
    getDocuments(
          folder,
          Config.search.resultsPerPage * Config.search.pageRangeDisplayed,
          initializeSortType(),
          initializeSortOrder(),
          0,
          true,
          initializeFilter()
    );
  }, [initializeFilter, initializeSortType, initializeSortOrder, clearDocuments, getDocuments]);

  const clearSearch = useCallback((folder) => {
    searchAfterNavigate(folder);
  }, [searchAfterNavigate]);

  
  const searchAfterSort = useCallback((folder) => {
    clearDocuments();
    if (USE_SEARCH_LOGS) console.log("useSearch => searchAfterSort => sortType, sortOrder", sortType, sortOrder);
    getDocuments(
          folder,
          Config.search.resultsPerPage * Config.search.pageRangeDisplayed,
          sortType,
          sortOrder,
          0,
          true
    );
  }, [clearDocuments, getDocuments, sortOrder, sortType]);

//#endregion

  return {
    filters,
    sortType,
    sortOrder,
    setSortType,
    setSortOrder,
    updateFilterValue,
    searchAfterFilter,
    searchAfterNavigate,
    clearSearch,
    searchAfterSort,
    getDocuments,
    fetchError,
    isSearching,
    documents,
    totalCountOfMatches,
    isFirstSearchComplete
  };
}

export { useSearch };